/** @format */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'state/store';
import { Col, Row, Progress, Alert } from 'antd';
import axios from 'axios';
import { Layout } from 'features/layout';
import { SaleSubmissionFormContainer } from 'features/saleSubmission';
import { setFormDisplay } from 'state/slice/buttonSlice';

interface LastSubmission {
  createdAt: string;
}

interface MonthlyStats {
  totalSales: number;
  totalMajorMed: number;
  totalprivateMed: number;
}

const AgentFormPage: React.FC = () => {
  const dispatch = useDispatch();
  const apiUrl = process.env.REACT_APP_SIADASH_BACKEND_URL;

  // Get user data from Redux
  const userData = useSelector((state: RootState) => (state.user as any).userData);
  const userId = userData?._id;
  const convosoUserId = userData?.convosoUserId;

  // Check if convosoUserId is missing
  const hasNoConvosoId = !convosoUserId || convosoUserId === '';

  // Get form display state from Redux
  const formDisplay = useSelector((state: RootState) => (state.button as any).formDisplay);

  const [lastSubmission, setLastSubmission] = useState<LastSubmission | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);

  // Fetch last policy submission using userId from userDetails
  useEffect(() => {
    const fetchLastSubmission = async () => {
      if (!userId) return;
      
      try {
        const response = await axios.get(
          `${apiUrl}/api/insurance-policies/last-submission/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
        setLastSubmission(response.data);
      } catch (error) {
        console.error('Error fetching last submission:', error);
      }
    };
    fetchLastSubmission();
  }, [apiUrl, userId]);

  // Fetch monthly stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${apiUrl}/api/stats/agent/monthly/sales-premium`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setMonthlyStats(response.data);
      } catch (err) {
        console.error('Error fetching sales data:', err);
      }
    };
    fetchData();
  }, [apiUrl]);

  const getProgressColor = (totalSales: number): string => {
    return totalSales < 80 ? '#95A4FC' : '#A1E3CB';
  };

  const getStatus = (totalSales: number): string => {
    return totalSales < 80 ? 'In Progress' : 'Complete';
  };

  const handleDisplay = (type: string) => {
    dispatch(setFormDisplay(type));
  };

  const handleHideForm = () => {
    dispatch(setFormDisplay('table'));
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        {hasNoConvosoId && (
          <Alert
            message="Missing Convoso User ID"
            description="Your account does not have a Convoso User ID configured. Please contact support to have this set up."
            type="warning"
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}
        {formDisplay === 'table' && (
          <Row className='form-link' gutter={48}>
            <Col
              xs={12}
              sm={8}
              md={8}
              lg={6}
              xl={6}
              onClick={() => handleDisplay('Sub')}
              className='form-card-wrapper'
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span className='form-card-Title'>Sales Submission Form</span>
                {lastSubmission && (
                  <span style={{ color: 'rgba(28, 28, 28, 0.40)' }}>
                    Last Submission:{' '}
                    {new Date(lastSubmission.createdAt).toLocaleString()}
                  </span>
                )}
              </div>

              {monthlyStats && (
                <>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: getProgressColor(monthlyStats.totalSales),
                          height: '10px',
                          width: '10px',
                        }}
                        className='lead-indicator'
                      ></div>
                      <p
                        style={{
                          color: getProgressColor(monthlyStats.totalSales),
                        }}
                      >
                        {getStatus(monthlyStats.totalSales)}
                      </p>
                    </div>
                    <Progress
                      percent={Math.min(monthlyStats.totalSales, 100)}
                      strokeColor={getProgressColor(monthlyStats.totalSales)}
                      status='active'
                    />

                    <div style={{ marginTop: '5px' }}>
                      {monthlyStats.totalSales}{' '}
                      <span style={{ color: 'rgba(28, 28, 28, 0.20)' }}>/</span>{' '}
                      100{' '}
                      <span style={{ color: 'rgba(28, 28, 28, 0.20)' }}>
                        Sales Goal
                      </span>
                    </div>
                  </div>
                </>
              )}
              <div className='card-line'></div>
              {monthlyStats && (
                <div className='deals-wrapper'>
                  <div>
                    <span style={{ color: 'rgba(28, 28, 28, 0.20)' }}>ACA</span>
                    <span>{monthlyStats.totalMajorMed}</span>
                  </div>
                  <div>
                    <span style={{ color: 'rgba(28, 28, 28, 0.20)' }}>U65</span>
                    <span>{monthlyStats.totalprivateMed}</span>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        )}

        {formDisplay === 'Sub' && (
          <SaleSubmissionFormContainer hideForm={handleHideForm} showUserSelection={false} />
        )}
      </div>
    </Layout>
  );
};

export default AgentFormPage;
